import { useCallback, useEffect, useRef } from 'react';

import camelcaseKeys from 'camelcase-keys';
import { v4 as uuid } from 'uuid';

import { useSubscription } from './use_subscription';

interface OneOffHandlers<T> {
  connected?: (requestId: string) => void | Promise<void>;
  // No answer is coming: either `connected` threw, or the idle timeout expired with no frame. The
  // Error says which, for callers that care; most just give up either way.
  connectedError?: (error: unknown) => void;
  disconnected?: () => void;
  rejected?: () => void;
  received: (payload: T) => void;
}

interface SubscribeOptions<T> {
  camelize?: boolean;
  autoUnsubscribe?: boolean;
  isTerminal?: (payload: T) => boolean;
  // 0 opts out
  idleTimeoutMs?: number;
}

// Workers behind OneOffChannel run with retry: false, so a killed process or a dropped socket can end
// a turn with no terminal frame. Every frame pushes this back, so for a streaming surface it caps
// silence - but where a surface only ever gets one frame, it caps the whole job.
const DEFAULT_IDLE_TIMEOUT_MS = 120_000;

// useSubscription is a simple wrapper - this is a bit more opinionated, and tied to OneOffChannel
// to prevent a backend boilerplate proliferation
export function useOneOffSubscription() {
  const { subscribe, unsubscribe } = useSubscription();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // useSubscription tears the socket down on unmount by calling its own unsubscribe, which cannot
  // know about this timer - so a pending one would outlive the component and fire into a dead one.
  useEffect(() => clearIdleTimer, [clearIdleTimer]);

  const stop = useCallback(() => {
    clearIdleTimer();
    unsubscribe();
  }, [clearIdleTimer, unsubscribe]);

  const subscribeOneOff = useCallback(
    <T>(
      handlers: OneOffHandlers<T>,
      {
        camelize = true,
        autoUnsubscribe = true,
        isTerminal,
        idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
      }: SubscribeOptions<T> = {}
    ) => {
      const requestId = uuid();
      let started = false;

      // Armed before the socket opens rather than on `connected`, so a subscription that never
      // connects is covered too.
      function restartIdleTimer() {
        if (!idleTimeoutMs) {
          return;
        }

        clearIdleTimer();
        idleTimerRef.current = setTimeout(() => {
          stop();
          handlers.connectedError?.(new Error(`No frame for ${idleTimeoutMs}ms`));
        }, idleTimeoutMs);
      }

      restartIdleTimer();
      subscribe(
        { channel: 'OneOffChannel', request_id: requestId },
        {
          ...handlers,
          // ActionCable calls this again on every reconnect, and running it twice would start the
          // request's job a second time under the same request id.
          async connected() {
            if (started) return;
            started = true;

            try {
              await handlers.connected?.(requestId);
            } catch (error) {
              stop();
              handlers.connectedError?.(error);
            }
          },
          rejected() {
            // A rejected subscription is dead, so nothing will ever reset the idle timer - leaving it
            // armed would fire the failure path a second time, after the caller has already given up.
            stop();
            handlers.rejected?.();
          },
          received(payload: object) {
            restartIdleTimer();
            const data = (camelize ? camelcaseKeys(payload, { deep: true }) : payload) as T;
            handlers.received(data);

            const shouldUnsubscribe = isTerminal ? isTerminal(data) : autoUnsubscribe;
            if (shouldUnsubscribe) {
              stop();
            }
          },
        }
      );
    },
    [clearIdleTimer, stop, subscribe]
  );

  return { subscribe: subscribeOneOff, unsubscribe: stop };
}
